import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { IoMdDownload } from "react-icons/io";
import { CircularProgress } from "@material-ui/core";
import axios from "axios";
import DatePicker from "../SharedComponents/DatePicker/DatePicker";
import "./Reports.css";

const REPORT_FILENAME = "boxexceptionreport.csv";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return formatDate(date);
};

const getDefaultToDate = () => formatDate(new Date());

const getMinFromDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return formatDate(date);
};

export const BoxExceptionReportCard = () => {
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  const minFromDate = getMinFromDate();
  const isFromDateTooOld = Boolean(fromDate) && fromDate < minFromDate;
  const isValidRange =
    Boolean(fromDate) && Boolean(toDate) && fromDate <= toDate && !isFromDateTooOld;
  const showRangeError = Boolean(fromDate) && Boolean(toDate) && fromDate > toDate;

  const handleDownload = () => {
    if (loading || !isValidRange) {
      return;
    }
    setLoading(true);
    setError(null);

    let passData;
    try {
      passData = JSON.parse(sessionStorage.getItem("passData"));
      if (!passData?.user) {
        throw new Error("Missing user in passData");
      }
    } catch (err) {
      setError("Unable to download the report. Please try again.");
      console.error("BoxExceptionReport download failed", {
        fromDate,
        toDate,
        status: err?.response?.status,
      });
      setLoading(false);
      return;
    }

    axios
      .get(
        `${global.REACT_APP_API_ENDPOINT}/reports/exportBoxExceptionReport/${passData.user}`,
        {
          params: { fromDate, toDate },
          responseType: "blob",
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", REPORT_FILENAME);
        document.body.appendChild(link);
        link.click();
        setLoading(false);
      })
      .catch((err) => {
        setError("Unable to download the report. Please try again.");
        console.error("BoxExceptionReport download failed", {
          fromDate,
          toDate,
          status: err?.response?.status,
        });
        setLoading(false);
      });
  };

  return (
    <Card className="report-card box-exception-report-card">
      <div className="report-card-body">
        <h3 className="report-card-body-header">Box Exception Report</h3>
        <div className="report-card-content">
          <p className="report-body-content">
            Box vendor document-transfer results, including successes,
            failures, and failure reasons, for the selected date range. Defaults to the last 30 days.
          </p>
          <div className="report-body-datepickers">
            <DatePicker
              id="boxExceptionReportFromDate"
              name="boxExceptionReportFromDate"
              label="From Date *"
              placeholder="Select Date"
              changeHandler={handleFromDateChange}
              value={fromDate}
              datePickerWidth="150px"
              min={minFromDate}
              max={toDate}
            />
            <DatePicker
              id="boxExceptionReportToDate"
              name="boxExceptionReportToDate"
              label="To Date *"
              placeholder="Select Date"
              changeHandler={handleToDateChange}
              value={toDate}
              datePickerWidth="150px"
              min={fromDate}
            />
          </div>
          {showRangeError && (
            <div className="report-body-content">
              From date must be on or before To date.
            </div>
          )}
          {isFromDateTooOld && !showRangeError && (
            <div className="report-body-content">
              From date cannot be more than 6 months in the past.
            </div>
          )}
          {error && <div className="report-body-content">{error}</div>}
          <div className="report-body-btn">
            {!loading && (
              <button
                className="download-btn"
                disabled={!isValidRange}
                onClick={handleDownload}
              >
                <IoMdDownload className="download-icon" /> Download
              </button>
            )}
            {loading && (
              <button className="download-btn" disabled>
                <CircularProgress
                  style={{
                    display: "inline-flex",
                    padding: "10px",
                    color: "#1176b5",
                  }}
                />
                Download
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
